import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Globe, MousePointer2, Type, Timer, Table2, Save, Lightbulb, Sparkles } from "lucide-react";
import RecorderDemo from "./RecorderDemo";

const STEPS = [
  {
    n: 1,
    icon: Globe,
    title: "Cola o URL e Iniciar sessão",
    body: "Abre uma sessão de browser real (Playwright). O ecrã ao vivo aparece em baixo. Funciona com sites públicos OU com áreas com login (vais preencher email/password em passos seguintes).",
    color: "text-sky-600 bg-sky-500/10 ring-sky-500/30",
  },
  {
    n: 2,
    icon: MousePointer2,
    title: "Escolhe um modo e age na captura de ecrã",
    body: "Clicar (botões/links), Preencher (inputs — email, password, datas), Esperar (1–3 s entre acções para a página carregar), Extrair (selecciona a tabela final).",
    color: "text-violet-600 bg-violet-500/10 ring-violet-500/30",
  },
  {
    n: 3,
    icon: Timer,
    title: "Repete até chegares à tabela",
    body: "Adiciona quantos passos forem precisos: login → filtros → \"Pesquisar\" → espera → extrair. Cada passo gravado aparece no painel da direita e podes editar/reordenar.",
    color: "text-amber-600 bg-amber-500/10 ring-amber-500/30",
  },
  {
    n: 4,
    icon: Save,
    title: "Guarda a rotina",
    body: "Dá-lhe um nome (ex: \"CRM Rex 50ES manutenções\"). Fica disponível em Rotinas para correres manualmente, agendares (cron), ou exportares em CSV/JSON/XLSX.",
    color: "text-emerald-600 bg-emerald-500/10 ring-emerald-500/30",
  },
];

const MODES = [
  { id: 'click', icon: MousePointer2, label: 'Clicar', tip: 'Para botões, links, abas. Ex: "Entrar", "Pesquisar", linha de tabela.', color: 'text-[hsl(var(--step-click))] bg-[hsl(var(--step-click))]/10' },
  { id: 'fill', icon: Type, label: 'Preencher', tip: 'Para inputs: email, password, NIF, datas, filtros de texto.', color: 'text-[hsl(var(--step-fill))] bg-[hsl(var(--step-fill))]/10' },
  { id: 'wait', icon: Timer, label: 'Esperar', tip: 'Insere uma pausa (1–3 s) para a página carregar antes do próximo passo.', color: 'text-[hsl(var(--step-wait))] bg-[hsl(var(--step-wait))]/10' },
  { id: 'extract', icon: Table2, label: 'Extrair', tip: 'Selecciona a tabela final cujos dados queres recolher.', color: 'text-[hsl(var(--step-extract))] bg-[hsl(var(--step-extract))]/10' },
];

export default function RecorderGuide({ initiallyOpen = true, onStartTour }) {
  const [open, setOpen] = useState(initiallyOpen);

  return (
    <Card className="overflow-hidden" data-testid="recorder-guide">
      <div className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 flex-1 text-left"
          data-testid="recorder-guide-toggle"
        >
          <div className="h-7 w-7 rounded-full bg-primary/10 text-primary grid place-items-center">
            <Lightbulb className="h-4 w-4"/>
          </div>
          <div>
            <div className="text-sm font-semibold">Como criar uma nova rotina</div>
            <div className="text-[11px] text-muted-foreground">4 passos · clica para {open ? 'esconder' : 'ver'} o guia</div>
          </div>
        </button>
        <div className="flex items-center gap-2">
          {onStartTour && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onStartTour(); }} data-testid="recorder-guide-tour-button">
              <Sparkles className="h-3.5 w-3.5 mr-1 text-primary"/> Tour interativo
            </Button>
          )}
          <button type="button" onClick={() => setOpen(!open)} className="h-7 w-7 grid place-items-center rounded-md hover:bg-muted">
            {open ? <ChevronUp className="h-4 w-4 text-muted-foreground"/> : <ChevronDown className="h-4 w-4 text-muted-foreground"/>}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-border bg-muted/20">
          {/* Demo animada + 4 steps lado a lado em desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-3 mt-3">
            <RecorderDemo/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {STEPS.map((s) => (
                <div key={s.n} className="rounded-xl border border-border bg-card p-3 flex flex-col gap-2 shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className={`h-7 w-7 rounded-full ring-1 grid place-items-center font-semibold text-[12px] ${s.color}`}>
                      {s.n}
                    </div>
                    <s.icon className="h-4 w-4 text-muted-foreground"/>
                    <div className="text-sm font-semibold leading-tight">{s.title}</div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Modes legend */}
          <div className="mt-4 rounded-xl border border-border bg-card p-3">
            <div className="text-xs font-semibold mb-2 text-foreground">Os 4 modos disponíveis</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
              {MODES.map(m => (
                <div key={m.id} className="flex items-start gap-2 rounded-lg border border-border/70 px-2.5 py-2">
                  <div className={`h-7 w-7 rounded-md grid place-items-center shrink-0 ${m.color}`}>
                    <m.icon className="h-4 w-4"/>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold">{m.label}</div>
                    <div className="text-[11px] text-muted-foreground leading-snug">{m.tip}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pro tip */}
          <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            <span className="font-semibold">Dica:</span> para áreas com login, a ordem típica é{" "}
            <span className="font-mono text-[11px]">Preencher email → Preencher password → Clicar Entrar → Esperar 2 s → (navegar) → Extrair</span>.
            Em sites com filtro de data, marca o input como tipo "data" (no passo gravado) — o sistema dispara automaticamente o evento <span className="font-mono">change</span> do datepicker.
          </div>
        </div>
      )}
    </Card>
  );
}

export function RecorderTemplates({ onApply }) {
  const TEMPLATES = [
    {
      id: 'public',
      label: 'Tabela pública',
      desc: 'Lista simples sem login (ex: catálogo, preços, etc.)',
      icon: Table2,
      payload: { url: 'https://example.com', steps: [
        { type: 'wait', ms: 1500, label: 'Esperar página carregar' },
        { type: 'extract', label: 'Extrair tabela maior', selector: null },
      ]}
    },
    {
      id: 'login',
      label: 'App com login',
      desc: 'Email + password → entrar → extrair listagem',
      icon: Type,
      payload: { url: 'https://example.com/login', steps: [
        { type: 'fill', label: 'Email', value: '__EMAIL__' },
        { type: 'fill', label: 'Palavra-passe', value: '__PASSWORD__' },
        { type: 'click', label: 'Entrar' },
        { type: 'wait', ms: 2000, label: 'Esperar dashboard' },
        { type: 'extract', label: 'Extrair listagem' },
      ]}
    },
    {
      id: 'date-filter',
      label: 'Tabela com filtro de data',
      desc: 'Login → filtros de data → pesquisar → extrair',
      icon: Timer,
      payload: { url: 'https://example.com/login', steps: [
        { type: 'fill', label: 'Email' },
        { type: 'fill', label: 'Palavra-passe' },
        { type: 'click', label: 'Entrar' },
        { type: 'wait', ms: 2000 },
        { type: 'click', label: 'Navegar para o relatório' },
        { type: 'wait', ms: 1500 },
        { type: 'fill', label: 'Data início', is_date: true, role: 'start' },
        { type: 'fill', label: 'Data fim', is_date: true, role: 'end' },
        { type: 'click', label: 'Pesquisar' },
        { type: 'wait', ms: 2000 },
        { type: 'extract', label: 'Extrair tabela final', header_contains: '' },
      ]}
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-muted-foreground mr-1">Começar de um exemplo:</span>
      {TEMPLATES.map(t => (
        <Button
          key={t.id}
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={() => onApply?.(t)}
          data-testid={`recorder-template-${t.id}`}
          title={t.desc}
        >
          <t.icon className="h-3.5 w-3.5 mr-1.5"/>
          {t.label}
        </Button>
      ))}
    </div>
  );
}
